import { Component, computed, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { LayoutService } from '@/layout/service/layout.service';
import { CredentialsService } from '@/features/auth/data-access/credentials.service';
import { AppService } from '@/shared/services/app.service';

@Component({
    selector: '[app-profilesidebar]',
    imports: [FormsModule, ButtonModule, DrawerModule, DialogModule, InputTextModule, PasswordModule],
    template: `
        <p-drawer [visible]="visible()" (onHide)="onDrawerHide()" position="right" [transitionOptions]="'.3s cubic-bezier(0, 0, 0.2, 1)'" styleClass="layout-profile-sidebar w-full sm:w-25rem">
            <div class="grid gap-5">
                <div class="rounded-2xl border border-surface-200 bg-surface-50/70 p-4 dark:border-surface-700 dark:bg-surface-900/40">
                    <div class="grid gap-1">
                        <span class="text-sm font-semibold text-surface-900 dark:text-surface-0">Account</span>
                        <span class="text-lg font-medium text-surface-600 dark:text-surface-300">{{ credentialsService.name() || 'Merchant' }}</span>
                    </div>
                </div>

                <button
                    type="button"
                    class="flex w-full items-center gap-3 rounded-2xl border border-surface-200 bg-white px-4 py-4 text-left transition-colors hover:border-primary-200 hover:bg-primary-50/60 dark:border-surface-700 dark:bg-surface-900 dark:hover:border-primary-700 dark:hover:bg-primary-950/20"
                    (click)="openChangePasswordDialog()"
                >
                    <span class="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary dark:bg-primary-950/40">
                        <i class="pi pi-key"></i>
                    </span>
                    <span class="font-medium">Change Password</span>
                </button>

                <button
                    type="button"
                    class="flex w-full items-center gap-3 rounded-2xl border border-red-200 bg-white px-4 py-4 text-left text-red-600 transition-colors hover:bg-red-50 dark:border-red-900/40 dark:bg-surface-900 dark:hover:bg-red-900/10"
                    (click)="logout()"
                >
                    <span class="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-600 dark:bg-red-900/20">
                        <i class="pi pi-sign-out"></i>
                    </span>
                    <span class="font-medium">Logout</span>
                </button>
            </div>
        </p-drawer>

        <p-dialog
            [(visible)]="changePasswordVisible"
            [modal]="true"
            header="Change Password"
            [draggable]="false"
            [resizable]="false"
            [style]="{ width: '32rem', maxWidth: 'calc(100vw - 2rem)' }"
            (onHide)="closeChangePasswordDialog()"
        >
            <div class="grid gap-4 pt-2">
                <div class="rounded-2xl border border-surface-200 bg-surface-50/80 p-4 dark:border-surface-700 dark:bg-surface-900/40">
                    <div class="flex items-start gap-3">
                        <span class="mt-0.5 flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary dark:bg-primary-950/40">
                            <i class="pi pi-shield"></i>
                        </span>
                        <div class="grid gap-1">
                            <span class="font-medium text-surface-900 dark:text-surface-0">Update your account password</span>
                            <span class="text-sm text-surface-500 dark:text-surface-400">Use a new password with at least 6 characters.</span>
                        </div>
                    </div>
                </div>

                <div class="grid gap-2">
                    <label for="current-password" class="text-sm font-medium">Current Password</label>
                    <p-password
                        inputId="current-password"
                        [feedback]="false"
                        [toggleMask]="true"
                        [ngModel]="currentPassword"
                        (ngModelChange)="currentPassword = $event || ''"
                        placeholder="Enter your current password"
                        styleClass="w-full"
                        inputStyleClass="w-full"
                    />
                </div>

                <div class="grid gap-2">
                    <label for="new-password" class="text-sm font-medium">New Password</label>
                    <p-password
                        inputId="new-password"
                        [feedback]="false"
                        [toggleMask]="true"
                        [ngModel]="newPassword"
                        (ngModelChange)="newPassword = $event || ''"
                        placeholder="Enter your new password"
                        styleClass="w-full"
                        inputStyleClass="w-full"
                    />
                    <small class="text-surface-500 dark:text-surface-400">Minimum 6 characters.</small>
                </div>

                @if (changePasswordError) {
                    <div class="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-900/40 dark:bg-red-950/20">
                        {{ changePasswordError }}
                    </div>
                }
            </div>

            <ng-template #footer>
                <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <p-button label="Cancel" severity="secondary" [outlined]="true" [disabled]="changingPassword" (onClick)="closeChangePasswordDialog()"></p-button>
                    <p-button label="Save Password" icon="pi pi-check" [loading]="changingPassword" [disabled]="changingPassword" (onClick)="submitChangePassword()"></p-button>
                </div>
            </ng-template>
        </p-dialog>
    `
})
export class AppProfileSidebar {
    credentialsService = inject(CredentialsService);
    private readonly appService = inject(AppService);

    changePasswordVisible = false;
    changingPassword = false;
    currentPassword = '';
    newPassword = '';
    changePasswordError: string | null = null;

    constructor(public layoutService: LayoutService) {}

    visible = computed(() => this.layoutService.layoutState().profileSidebarVisible);

    onDrawerHide() {
        this.layoutService.layoutState.update((state) => ({
            ...state,
            profileSidebarVisible: false
        }));
    }

    openChangePasswordDialog(): void {
        this.changePasswordVisible = true;
        this.currentPassword = '';
        this.newPassword = '';
        this.changePasswordError = null;
    }

    closeChangePasswordDialog(): void {
        this.changePasswordVisible = false;
        this.changingPassword = false;
        this.currentPassword = '';
        this.newPassword = '';
        this.changePasswordError = null;
    }

    submitChangePassword(): void {
        const currentPassword = this.currentPassword.trim();
        const newPassword = this.newPassword.trim();

        if (!currentPassword || !newPassword) {
            this.changePasswordError = 'Current password and new password are required.';
            return;
        }
        if (newPassword.length < 6) {
            this.changePasswordError = 'New password must be at least 6 characters.';
            return;
        }

        this.changingPassword = true;
        this.changePasswordError = null;
        this.credentialsService.setPassword(currentPassword, newPassword).subscribe({
            next: () => {
                this.appService.toastSuccess('Set new password');
                this.closeChangePasswordDialog();
            },
            error: (error) => {
                this.changePasswordError = 'Cannot change password.';
                this.appService.toastError('Change password failed', error);
                this.changingPassword = false;
            }
        });
    }

    logout(): void {
        this.appService.confirm('Logout', () => {
            this.credentialsService.logout();
        });
    }
}
